import { setupCache } from "axios-cache-adapter";
import Axios from "axios";
import jwt from "jsonwebtoken";
import { AUTH_SERVICE, DATA_API_SERVICE } from "@/appConfig";
import { UserToken } from "@/api/tokens";
import router from "@/router";

// Create `axios-cache-adapter` instance
export const cache = setupCache({
  maxAge: 15 * 1000,
  invalidate: async (config, request) => {
    // Don't do anything if there's no method, or we want to ignore cache
    if (!request.method) {
      return;
    }
    if (request.cache?.ignoreCache) {
      return;
    }

    // Clear specific entry
    if (!request.clearCacheEntry !== undefined) {
      if (request.clearCacheEntry) {
        await (config.store as any).removeItem((config as any).uuid);
      }
    }

    // On POST, DELETE, or UPDATE we should invalidate the cache
    // Don't clear out the whole cache if the user doesn't want to
    if (request.ignoreClearAllCheck) {
      return;
    }
    const requestMethod = request.method.toUpperCase();
    if (["POST", "DELETE", "PUT"].includes(requestMethod)) {
      (cache.store as any).clear();
    }
  }
});

const API = Axios.create({
  adapter: cache.adapter
});

const baseUrlsToUseAuthHeaders = [DATA_API_SERVICE];

API.interceptors.request.use(async config => {
  if (!config.url) return config;
  let path = config.url;
  baseUrlsToUseAuthHeaders.forEach(url => {
    if (config.url!.startsWith(url)) {
      path = config.url!.replace(DATA_API_SERVICE, "");
    }
  });
  if (path === "/login/web" || path === "/realm") return config;

  // Check for expired jwt
  let accessToken = UserToken.get();
  const refreshToken = UserToken.getRefresh();
  if (accessToken && refreshToken) {
    const decoded = jwt.decode(accessToken);
    // JWT exp is in seconds, JS dates use milliseconds
    if (new Date(decoded.exp * 1000) < new Date()) {
      // Expired JWT, refresh here
      const response = await AUTH.post(`${AUTH_SERVICE}/refresh`, {
        // eslint-disable-next-line @typescript-eslint/camelcase
        access_token: accessToken,
        // eslint-disable-next-line @typescript-eslint/camelcase
        refresh_token: refreshToken
      });
      UserToken.update(response.data.access_token);
      accessToken = response.data.access_token;
    }
  }

  config.headers = {
    ...config.headers,
    Authorization: `Bearer ${accessToken}`
  };
  return config;
});

API.interceptors.response.use(
  config => config,
  error => {
    if (
      (error.response.status === 401 || error.response.status === 403) &&
      error.response.config.url.replace(DATA_API_SERVICE, "") !== "/v2/user/confirm"
    ) {
      UserToken.remove();
      switch (error.response.data.errorType) {
        case "InvalidRole": {
          router.push({
            name: "invalidPermission"
          });
          return;
        }
        default: {
          return;
        }
      }
    } else if (error.response.status === 400) {
      if (error.response.data.message === "User not found") {
        router.push({
          name: "invalidPermission"
        });
        return;
      } else if (error.response.data.message.includes("stations_sqrcode_uindex")) {
        return error.response;
      } else {
        return;
      }
    }
    throw error;
  }
);

const AUTH = Axios.create();
AUTH.defaults.withCredentials = true;

AUTH.interceptors.response.use(
  config => config,
  error => {
    if (
      error.response.status === 401 &&
      // Don't redirect for login requests
      !(
        error.response.config.url.replace(AUTH_SERVICE, "") === "/login/web" ||
        error.response.config.url.includes("mfa")
      )
    ) {
      UserToken.remove();
      router.push({
        name: "sessionEnded"
      });
    } else {
      throw error;
    }
  }
);

export default API;

export { AUTH };
